import { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import OnboardingTutorial, { useOnboardingTutorial } from "../../components/feature/OnboardingTutorial";
import RandomEventModal from "./components/RandomEventModal";
import AdminEventModal, { type AdminEventData } from "./components/AdminEventModal";
import RankUpModal, { getRepTier } from "./components/RankUpModal";
import RankMissionPanel from "./components/RankMissionPanel";
import WeeklyReportPanel from "./components/WeeklyReportPanel";
import NPCChatModal from "../../components/feature/NPCChatModal";
import { useTodoContext } from "../../contexts/TodoContext";
import { useAiCEO } from "../../hooks/useAiCEO";
import TodoList from "./components/TodoList";
import RecentActivity from "./components/RecentActivity";
import HubProgressPanel from "./components/HubProgressPanel";
import TeamPanel from "./components/TeamPanel";
import PendingEventsPanel from "./components/PendingEventsPanel";
import EtiquetteTipCard from "./components/EtiquetteTipCard";
import { CEODashboard, ARDashboard, FinanceDashboard, MarketingDashboard, LegalDashboard, LiveCEODashboard, FoundationCEODashboard, WebtoonCEODashboard, AgencyCEODashboard } from "./components/RoleDashboards";
import TeamDirectory from "./components/TeamDirectory";
import { randomEvents } from "../../mocks/dashboard";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { useRole } from "../../contexts/RoleContext";
import { supabase } from "../../lib/supabase";
import KPIPanel from "./components/KPIPanel";
import CheckInPanel from "./components/CheckInPanel";
import TeamKPIPanel from "./components/TeamKPIPanel";
import FinalPresentationPanel from "./components/FinalPresentationPanel";

export default function Dashboard() {
  const [showEvent, setShowEvent] = useState(false);
  const [dismissedEvents, setDismissedEvents] = useState<string[]>([]);
  const [lastEventIdx, setLastEventIdx] = useState(-1);
  const [notification, setNotification] = useState<{ text: string; type: "success" | "danger" } | null>(null);
  const [eventQueue, setEventQueue] = useState<number[]>([]);
  const [adminEvent, setAdminEvent] = useState<AdminEventData | null>(null);
  const [npcModal, setNpcModal] = useState<{ open: boolean; npcId: string; context: string }>({ open: false, npcId: "mentor", context: "" });
  const [pendingRefresh, setPendingRefresh] = useState(0);

  // 직급 상승 감지
  const prevRepRef = useRef<number | null>(null);
  const [rankUpData, setRankUpData] = useState<{ prevRank: string; newRank: string; reputation: number } | null>(null);

  const { show: showTutorial, setShow: setShowTutorial } = useOnboardingTutorial();
  const { companyState, logEvent, workspaceId } = useCompany();
  const { user, profile } = useAuth();
  const { jobRole, roleConfig, companyType } = useRole();
  const { addTodos } = useTodoContext();
  const { sendWelcomeMail, sendWeeklyCheckIn, sendEventFeedback, isPlugAndPlay } = useAiCEO();

  // Mode B: 입사 환영 메일 + 주간 체크인
  useEffect(() => {
    if (!isPlugAndPlay) return;
    const t1 = setTimeout(() => sendWelcomeMail(), 3000);
    const t2 = setTimeout(() => sendWeeklyCheckIn(), 30000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlugAndPlay]);

  useEffect(() => {
    const indices = Array.from({ length: randomEvents.length }, (_, i) => i).sort(() => Math.random() - 0.5);
    setEventQueue(indices);
  }, []);

  // 직급 상승 감지 — companyState.reputation 변화 추적
  useEffect(() => {
    if (!companyState) return;
    const newRep = companyState.reputation;
    if (prevRepRef.current !== null && newRep !== prevRepRef.current) {
      const prevTier = getRepTier(prevRepRef.current);
      const newTier = getRepTier(newRep);
      if (newTier.level > prevTier.level) {
        setRankUpData({ prevRank: prevTier.name, newRank: newTier.name, reputation: newRep });
      }
    }
    prevRepRef.current = newRep;
  }, [companyState?.reputation]);

  // 강사 이벤트 Realtime 구독
  useEffect(() => {
    if (!workspaceId) return;
    const channel = supabase
      .channel(`admin-events-${workspaceId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "role_notifications",
        filter: `workspace_id=eq.${workspaceId}`,
      }, (payload) => {
        const row = payload.new as { type: string; body: string; to_role: string; to_user_id?: string };
        const isForMe = row.to_role === "ALL"
          || row.to_role === jobRole
          || (row.to_role === "INDIVIDUAL" && row.to_user_id === user?.id);
        if (row.type === "admin_event" && isForMe) {
          try {
            const parsed = JSON.parse(row.body) as AdminEventData;
            setAdminEvent(parsed);
          } catch { /* ignore */ }
        }
      }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [workspaceId, jobRole, user?.id]);

  const triggerNextEvent = useCallback(() => {
    if (showEvent) return;
    setEventQueue((prev) => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      setLastEventIdx(next);
      setShowEvent(true);
      return rest.length > 0 ? rest : Array.from({ length: randomEvents.length }, (_, i) => i).sort(() => Math.random() - 0.5);
    });
  }, [showEvent]);

  useEffect(() => {
    const delay = 4000 + Math.random() * 4000;
    const timer = setTimeout(() => { if (dismissedEvents.length === 0) triggerNextEvent(); }, delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scheduleNext = () => {
      const interval = 45000 + Math.random() * 45000;
      return setTimeout(() => { triggerNextEvent(); scheduleNext(); }, interval);
    };
    const timer = scheduleNext();
    return () => clearTimeout(timer);
  }, [triggerNextEvent]);

  useEffect(() => {
    const handle = () => {
      if (document.visibilityState === "visible" && Math.random() > 0.5) setTimeout(() => triggerNextEvent(), 5000);
    };
    document.addEventListener("visibilitychange", handle);
    return () => document.removeEventListener("visibilitychange", handle);
  }, [triggerNextEvent]);

  // 이벤트 결과 처리
  const handleEventAction = async (repDelta: number, capDelta: number, chosenActionLabel?: string) => {
    if (lastEventIdx < 0) return;
    const ev = randomEvents[lastEventIdx];
    await logEvent(ev.id, ev.type, ev.headline, `rep:${repDelta},cap:${capDelta}`, capDelta, repDelta);
    const repText = repDelta > 0 ? `명성 +${repDelta}pt` : repDelta < 0 ? `명성 ${repDelta}pt` : "";
    const capText = capDelta > 0 ? `자본금 +${capDelta.toLocaleString()}만원` : capDelta < 0 ? `자본금 ${capDelta.toLocaleString()}만원` : "";
    setNotification({ text: `이벤트 결과: ${[repText, capText].filter(Boolean).join(", ")}`, type: repDelta >= 0 && capDelta >= 0 ? "success" : "danger" });
    setTimeout(() => setNotification(null), 4000);
    setDismissedEvents((prev) => [...prev, ev.id]);
    setShowEvent(false);
    if (isPlugAndPlay && chosenActionLabel) {
      setTimeout(() => {
        sendEventFeedback({ eventTitle: ev.headline, eventDescription: ev.description, chosenAction: chosenActionLabel, result: { reputation: repDelta, capital: capDelta } });
      }, 5000);
    }
  };

  // 팀 협의 → 이벤트 미결 처리
  const handleDeferEvent = useCallback((_eventIdx: number) => {
    setPendingRefresh(p => p + 1);
    setShowEvent(false);
  }, []);

  // 미결 이벤트 재오픈
  const handleReopenPending = useCallback((eventIdx: number) => {
    setLastEventIdx(eventIdx);
    setShowEvent(true);
  }, []);

  const hasNewEvent = eventQueue.length < randomEvents.length && !showEvent;

  const renderRoleDashboard = () => {
    if (jobRole === "CEO") {
      switch (companyType) {
        case "LiveEntertainment": return <LiveCEODashboard />;
        case "CulturalFoundation": return <FoundationCEODashboard />;
        case "WebtoonStudio": return <WebtoonCEODashboard />;
        case "ContentAgency": return <AgencyCEODashboard />;
        default: return <CEODashboard />;
      }
    }
    switch (jobRole) {
      case "AR": return <ARDashboard />;
      case "Finance": return <FinanceDashboard />;
      case "Marketing": return <MarketingDashboard />;
      case "Legal": return <LegalDashboard />;
      default: return <CEODashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar onEventClick={() => setShowEvent(true)} hasNewEvent={hasNewEvent} />
        <main className="flex-1 p-5 space-y-5">

          {notification && (
            <div className={`px-4 py-2.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
              notification.type === "success"
                ? "bg-white/[0.04] text-[#BBBBBB] border border-white/[0.08]"
                : "bg-white/[0.03] text-[#999999] border border-white/[0.05]"
            }`}>
              <i className={`${notification.type === "success" ? "ri-check-line" : "ri-alert-line"} text-base`}></i>
              {notification.text}
            </div>
          )}

          <div className="flex items-center justify-between pb-1 border-b border-white/[0.04]">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`${roleConfig.icon} text-[#444444] text-base`}></i>
              </div>
              <div>
                <h2 className="text-[#F2F2F2] font-medium text-sm">{profile?.display_name ?? "사용자"}님의 대시보드</h2>
                <p className="text-[#555555] text-xs">{roleConfig.label} — {roleConfig.labelEn}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-[#555555] text-[9px] uppercase tracking-widest">자본금</div>
                <div className="text-[#777777] text-sm tabular-nums font-medium">{(companyState?.capital ?? 0).toLocaleString()}만원</div>
              </div>
              <div className="text-right">
                <div className="text-[#555555] text-[9px] uppercase tracking-widest">명성</div>
                <div className="text-[#777777] text-sm tabular-nums font-medium">{companyState?.reputation ?? 0}pt</div>
              </div>
            </div>
          </div>

          {/* 팀 협의 미결 이벤트 패널 */}
          <PendingEventsPanel key={pendingRefresh} onReopen={handleReopenPending} />

          {renderRoleDashboard()}

          {/* 팀원 명부 */}
          <TeamDirectory />

          <CheckInPanel />
          <RankMissionPanel />
          <WeeklyReportPanel />
          <KPIPanel />
          <TeamKPIPanel />
          <FinalPresentationPanel />

          <div className="grid grid-cols-4 gap-4 mt-2">
            <div className="h-72"><TodoList /></div>
            <div className="h-72"><RecentActivity /></div>
            <div><HubProgressPanel /></div>
            <div className="space-y-4"><TeamPanel /><EtiquetteTipCard /></div>
          </div>
        </main>
      </div>

      {showEvent && lastEventIdx >= 0 && (
        <RandomEventModal
          eventIndex={lastEventIdx}
          onClose={() => { setShowEvent(false); if (lastEventIdx >= 0) setDismissedEvents((prev) => [...prev, randomEvents[lastEventIdx].id]); }}
          onAction={handleEventAction}
          onOpenNPC={(npcId, context) => setNpcModal({ open: true, npcId, context })}
          onAddTodos={addTodos}
          onDefer={handleDeferEvent}
        />
      )}

      {adminEvent && (
        <AdminEventModal
          event={adminEvent}
          onClose={() => setAdminEvent(null)}
        />
      )}

      {rankUpData && (
        <RankUpModal
          prevRank={rankUpData.prevRank}
          newRank={rankUpData.newRank}
          reputation={rankUpData.reputation}
          onClose={() => setRankUpData(null)}
        />
      )}

      {showTutorial && <OnboardingTutorial onClose={() => setShowTutorial(false)} />}

      <NPCChatModal
        isOpen={npcModal.open}
        onClose={() => setNpcModal(prev => ({ ...prev, open: false }))}
        initialNpcId={npcModal.npcId}
        context={npcModal.context}
      />
    </div>
  );
}
